import { useCallback } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { CONFLUENCE } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useCloudName } from '@atlassian/jira-tenant-context-controller/src/components/cloud-name/index.tsx';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { SOURCE, type Source } from '../services/feature-flag/index.tsx';
import { useInvitees } from '../services/get-invitees/index.tsx';
import { getPageTitle } from '../services/get-page-title/index.tsx';
import { useCanInvite } from '../services/invite-capability/index.tsx';
import { useInviteUsers } from '../services/invite/index.tsx';
import { useOptOut } from '../services/opt-out/index.tsx';
import { isConfluenceLink, type LinkData } from '../utils/index.tsx';
import { invitePromptOnLinkInsertExperience } from './experiences/index.tsx';
import { useInviteErrorFlag } from './invite-error-flag/index.tsx';
import { useInvitePromptFlag } from './invite-prompt-flag/index.tsx';
import { useInviteSuccessFlag } from './invite-success-flag/index.tsx';

export const useInvitePrompt = (createIssueProjectKey?: string) => {
	const canInvite = useCanInvite();
	const getInvitees = useInvitees(createIssueProjectKey);
	const showInvitePromptFlag = useInvitePromptFlag();
	const showSuccessFlag = useInviteSuccessFlag();
	const showErrorFlag = useInviteErrorFlag();
	const inviteUsers = useInviteUsers();
	const { getOptOutStatus, setOptOutStatus, incrementViewCount, shouldShowOptOutOption } =
		useOptOut();
	return useCallback(
		async (continueUrl: string, data: LinkData, source: Source = SOURCE.ISSUE_VIEW) => {
			try {
				// eslint-disable-next-line jira/ff/unsafe-no-exposure
				const [config] = UNSAFE_noExposureExp('persistent_invite_on_link_insert');
				if (config.get('cohort', 'not-enrolled') === 'variation') return;

				invitePromptOnLinkInsertExperience.start();
				const { tenantId, product, name } = data;
				const hasOptedOut = await getOptOutStatus();
				if (hasOptedOut) {
					return;
				}

				const canInviteToProduct = await canInvite(tenantId, product);
				if (!canInviteToProduct) {
					return;
				}

				const users = await getInvitees(tenantId, product, 10);
				if (users.length === 0) {
					return;
				}

				const targetPageLabel = name || (await getPageTitle(continueUrl));

				const integration = 'invitePromptOnLink';

				showInvitePromptFlag({
					users,
					product,
					source,
					targetPageLabel,
					allowOptOut: await shouldShowOptOutOption(),
					onSubmit: async () => {
						try {
							const response = await inviteUsers({
								continueUrl,
								userIds: users.map(({ id }) => id),
								tenantId,
								product,
								integration,
							});

							showSuccessFlag({
								userIds: users.map(({ id }) => id),
								product,
								requireAdminApproval: response.accessRequested.length > 0,
							});
						} catch {
							showErrorFlag();
						}
					},
					onDismiss: incrementViewCount,
					onOptOut: setOptOutStatus,
				});

				invitePromptOnLinkInsertExperience.success();
				return true;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInvitePromptExperiment',
						packageName: 'jiraInvitePromptOnLinkInsert',
					},
					error,
				});
				invitePromptOnLinkInsertExperience.failure();
			}
		},
		[
			canInvite,
			getInvitees,
			getOptOutStatus,
			incrementViewCount,
			inviteUsers,
			setOptOutStatus,
			shouldShowOptOutOption,
			showErrorFlag,
			showInvitePromptFlag,
			showSuccessFlag,
		],
	);
};

export const useInviteOnCreateLink = (link: string) => {
	const cloudId = useCloudId();
	const cloudName = useCloudName();
	const triggerInviteExperiment = useInvitePrompt();
	return useCallback(() => {
		if (isConfluenceLink(link, cloudName)) {
			triggerInviteExperiment(link, { tenantId: cloudId, product: CONFLUENCE }, SOURCE.CREATE_LINK);
		}
	}, [cloudId, cloudName, link, triggerInviteExperiment]);
};
